/* The assurance queue.

   Tokens only (ADR-0007) — stylelint fails a colour literal. Nothing below 12px (`--t-min`).
   Colour tints an edge and never carries a meaning on its own (design rule 8): every state prints
   its name and whose move it is. */

.whoCan {
  margin: 0;
  padding: 10px 12px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--panel-2);
  font-size: var(--t-dense);
  color: var(--ink-2);
  line-height: 1.5;
  max-width: 68ch;
}

.whoCan strong {
  display: block;
  color: var(--ink);
  margin-bottom: 4px;
}

.note {
  margin: 0 0 10px;
  font-size: var(--t-min);
  color: var(--muted);
  line-height: 1.5;
  max-width: 80ch;
}

.stack {
  grid-column: 1 / -1;
  display: grid;
  gap: var(--gap);
  min-width: 0;
}

.tiles {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--gap);
  min-width: 0;
}

.layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 360px);
  gap: var(--gap);
  align-items: start;
  min-width: 0;
}

@media (width <= 1100px) {
  .layout {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (width <= 760px) {
  .tiles {
    grid-template-columns: minmax(0, 1fr);
  }
}

.engaged {
  padding: 8px 10px;
  border-left: 3px solid var(--warn-line);
  background: var(--warn-w);
  color: var(--ink-2);
}

.engaged strong {
  color: var(--ink);
}

/* ------------------------------------------------------------------------------ the filter */

.filters {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin: 0 0 12px;
}

.filter {
  padding: 3px 9px;
  font: inherit;
  font-size: var(--t-min);
  color: var(--ink-2);
  background: var(--panel);
  border: 1px solid var(--line-2);
  border-radius: 999px;
  cursor: pointer;
}

.filter:hover {
  border-color: var(--accent);
}

.filterOn {
  color: var(--ink);
  font-weight: 700;
  background: var(--accent-w);
  border-color: var(--accent);
}

.filterCount {
  font-family: var(--mono);
  color: var(--muted);
}

/* -------------------------------------------------------------------------------- a request */

.queue {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 12px;
}

.request {
  display: grid;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid var(--line);
  border-radius: 8px;
}

.requestHead {
  display: flex;
  gap: 10px;
  align-items: baseline;
  flex-wrap: wrap;
}

.kind {
  display: inline-block;
  font-size: var(--t-min);
  font-weight: 700;
  color: var(--accent);
}

.kind::first-letter {
  text-transform: uppercase;
}

.reference {
  font-family: var(--mono);
  font-size: var(--t-min);
  color: var(--ink-2);
}

/* The state is a `StatusPill` (icon and word) and the sentence beside it says whose move it is. */
.stateTag {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px 6px;
  margin-left: auto;
  font-size: var(--t-min);
  color: var(--ink-2);
  min-width: 0;
}

.whose {
  min-width: 0;
}

.question {
  margin: 0;
  font-size: var(--t-body);
  color: var(--ink);
  line-height: 1.5;
  white-space: pre-wrap;
}

.facts {
  margin: 0;
  font-size: var(--t-min);
  color: var(--ink-2);
  line-height: 1.6;
}

.id {
  font-family: var(--mono);
  font-size: var(--t-min);
  color: var(--muted);
  overflow-wrap: anywhere;
}

/* ---------------------------------------------------------------------------------- the thread */

.thread {
  display: grid;
  gap: 6px;
  padding: 8px 10px;
  border-radius: 6px;
  background: var(--panel-2);
}

.quietLine {
  margin: 0;
  font-size: var(--t-min);
  color: var(--muted);
}

.threadHead {
  margin: 0;
  font-size: var(--t-min);
  font-weight: 600;
  color: var(--ink-2);
}

.responses {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 8px;
}

.response {
  display: grid;
  gap: 3px;
  padding-left: 10px;
  border-left: 2px solid var(--line-2);
}

.responseMeta {
  margin: 0;
  font-size: var(--t-min);
  color: var(--muted);
}

.responseBody {
  margin: 0;
  font-size: var(--t-dense);
  color: var(--ink);
  line-height: 1.5;
  white-space: pre-wrap;
}

.citation {
  margin: 0;
  font-size: var(--t-min);
  color: var(--ink-2);
}

.withheld {
  padding: 4px 8px;
  border-left: 3px solid var(--warn-line);
  background: var(--warn-w);
}

.closure {
  margin: 0;
  font-size: var(--t-dense);
  color: var(--ink);
  line-height: 1.5;
}

/* ----------------------------------------------------------------------------------- the forms */

.actions {
  display: grid;
  gap: 10px;
}

.form {
  display: grid;
  gap: 4px;
}

.textarea {
  padding: 7px 9px;
  font: inherit;
  font-size: 13px;
  font-weight: 400;
  color: var(--ink);
  background: var(--panel);
  border: 1px solid var(--line-2);
  border-radius: 6px;
  resize: vertical;
  width: 100%;
  box-sizing: border-box;
}

.help {
  margin: 0 0 6px;
  font-size: var(--t-min);
  color: var(--muted);
  line-height: 1.5;
}

.buttons {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

/* The export link in the header, drawn like a secondary action. */
.exportLink {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: var(--h-ctl);
  padding: 0 12px;
  border: 1px solid var(--line-2);
  border-radius: var(--radius-ctl);
  background: var(--panel);
  color: var(--accent);
  font-size: var(--t-dense);
  text-decoration: none;
}

.exportLink:hover {
  border-color: var(--accent-line);
  background: var(--panel-2);
}
